import { useLayoutEffect, useRef } from "react";
import { flushSync } from "react-dom";

const PDF_SAMPLE_BYTES = 1024 * 1024;

export async function isPasswordProtectedPdf(blob: Blob, fileName?: string): Promise<boolean> {
	const lowerName = (fileName || "").toLowerCase();
	const lowerMime = (blob.type || "").toLowerCase();

	if (!lowerName.endsWith(".pdf") && !lowerMime.includes("pdf")) {
		return false;
	}

	try {
		const decoder = new TextDecoder();
    // Check start and end of file, since large files could take up
    // lots of space with headers, etc.
		const startText = decoder.decode(await blob.slice(0, PDF_SAMPLE_BYTES).arrayBuffer());
		const endText = decoder.decode(await blob.slice(Math.max(0, blob.size - PDF_SAMPLE_BYTES)).arrayBuffer());

		return /\/Encrypt\b/.test(startText) || /\/Encrypt\b/.test(endText);
	} catch {
		return false;
	}
}

export function usePasswordProtectedPdfPrompt(active: boolean, onCancel: () => void) {
	const onCancelRef = useRef(onCancel);
	onCancelRef.current = onCancel;

	useLayoutEffect(() => {
		if (!active || typeof window === "undefined") {
			return;
		}

		const originalPrompt = window.prompt;
		const cancelablePrompt: typeof window.prompt = (message, defaultValue) => {
			const response = originalPrompt.call(window, message, defaultValue);

			if (response === null) {
				flushSync(() => onCancelRef.current());
				throw new Error("PDF password entry cancelled");
			}

			return response;
		};

		window.prompt = cancelablePrompt;

		return () => {
			if (window.prompt === cancelablePrompt) {
				window.prompt = originalPrompt;
			}
		};
	}, [active]);
}